import { getSidebarItem, SidebarItemKey } from "../../../config/sidebarConfig";
import { HELP_VIDEOS, HELP_VIDEOS_PATH } from "../../help/helpContent";
import { openPageEntry } from "./openPage";
import { defineEntry } from "./types";
import type { AnswerEntry } from "./types";
import { countOf, listOf } from "./wording";

/**
 * "How do I…?": short steps in the screen's own labels, linking to the page
 * where it's done. Only offered to people who can open that page.
 */
function howToEntry(
  id: string,
  page: SidebarItemKey,
  steps: string | ((asked: string) => string),
  examples: string[]
): AnswerEntry {
  const { label, path } = getSidebarItem(page);
  const body = (asked = "") => (typeof steps === "string" ? steps : steps(asked));
  const lead = (prefix: string) => (_values: unknown, _slots: unknown, asked = "") => {
    const text = body(asked);
    // "Yes…", "No…", or a first sentence saying it can't be done: no cheerful "Here's how." in front
    return /^(?:yes|no)\b/i.test(text) || /^[^.]*\b(?:can't|cannot|isn't|doesn't|won't)\b/i.test(text) ? text : `${prefix} ${text}`;
  };
  return {
    id: `helpCenter.${id}`,
    sidebarItem: "helpCenter",
    alsoNeeds: [page],
    examples,
    answer: {
      load: async () => ({}),
      templates: [(_values, _slots, asked) => body(asked), lead("Here's how."), lead("It's quick.")],
      link: () => ({ label: `Open ${label}`, href: path }),
    },
  };
}

const EDIT_SENT_STEPS =
  "A sent Notice can't be edited. Within 30 minutes, Remove can take it down, though some parents may already have seen it. To send a corrected one, find it on Notices under Sent and press Duplicate. It opens in Create with the details filled in, ready to review and publish. A Notice still under Scheduled can be moved: press Edit beside it to change when it goes out.";

function editSentSteps(asked: string): string {
  const copying = /\b(?:copy|duplicate)\b/i.test(asked) && !/\b(?:unsend|typo|fix|change|correct|update|edit)\b/i.test(asked);
  if (copying) return "Yes. Open Notices, select Sent, and press Duplicate. That copies it into Create for you to review and publish.";
  // "so I can't edit it at all?", "can I edit a sent notice?": asked yes or no, so answered "No" first
  return /^(?:(?:so|and|but|ok)[,\s]+)*(?:(?:can|could|is|am|do)\b|i\s+can'?t\b)|\bat all\b/i.test(asked.trim()) && /\b(?:edit|change|fix)\b/i.test(asked)
    ? `No. ${EDIT_SENT_STEPS}`
    : EDIT_SENT_STEPS;
}

const coursesEntry: AnswerEntry = {
  id: "helpCenter.courses",
  sidebarItem: "helpCenter",
  examples: [
    "Go to courses",
    "Open courses",
    "Show me courses",
    "Where are the courses?",
    "Take me to courses",
    "Open the courses page",
    "Show the courses section",
    "Go to the courses tab",
    "Open course list",
    "Take me to the courses page",
    "teach me noticeboard",
    "where are the tutorials",
    "Is there something I can read to learn this?",
    "Where do I open the lessons?",
  ],
  answer: {
    load: async () => ({}),
    templates: [() => "Explore the courses to learn how to use Noticeboard."],
    link: () => ({ label: "Go to courses", href: "/help/courses" }),
  },
};

/** Help Center's Video Tutorials, as that page lists them. */
const videosEntry = defineEntry<{ titles: string[] }>({
  id: "helpCenter.videos",
  sidebarItem: "helpCenter",
  examples: [
    "Do we have videos?",
    "Where are the how-to videos?",
    "Where are the tutorial videos?",
    "Where are the training videos?",
    "tutorials",
    "tutorial",
    "Do we have any videos?",
    "Are there videos?",
    "Where are the videos?",
    "Show me the videos",
    "What videos do we have?",
    "Are there tutorial videos?",
    "Do you have how-to videos?",
    "Is there a video I can watch?",
    "What tutorial videos are there?",
    "Can I watch a video?",
    "Do we have video tutorials?",
    "What videos can I watch?",
    "Are there any videos to watch?",
    "video on how to create a notice",
    "wheres the training video",
    "browse tutorials",
  ],
  answer: {
    load: async () => ({ titles: HELP_VIDEOS.map((video) => video.title) }),
    templates: [
      ({ titles }) => `Yes. Help Center has ${countOf(titles.length, "tutorial video")}: ${listOf(titles)}.`,
      ({ titles }) => `There are ${countOf(titles.length, "video")} in Help Center: ${listOf(titles)}.`,
      ({ titles }) => `You can watch ${countOf(titles.length, "tutorial video")} in Help Center: ${listOf(titles)}.`,
    ],
    link: () => ({ label: "Open Video Tutorials", href: HELP_VIDEOS_PATH }),
  },
});

export const helpCenterEntries = [
  coursesEntry,
  videosEntry,
  openPageEntry("helpCenter", "It's where you watch tutorials and learn how Noticeboard works.", [
    "Where can I watch a tutorial?",
    "watch a tutorial",
    "Where do I get help?",
    "Show me how-to videos",
  ]),

  howToEntry(
    "scheduleNotice",
    "create",
    "Yes. Start your Notice on Create. On the last step, choose Schedule for later, set the publish date and time, then press Schedule. It then appears under Notices → Scheduled.",
    [
      "How do I schedule a notice to go out later?",
      "How do I schedule a notice?",
      "Can I send a notice later?",
      "How do I send a notice at a set time?",
      "Can I schedule a notice for tomorrow morning?",
      "How do I post a notice later instead of now?",
      "How do I set a time for a notice to go out?",
      "Can a notice go out automatically on a certain day?",
      "How do I delay a notice?",
      "Can I delay a notice?",
      "Schedule a notice for later",
    ]
  ),

  howToEntry(
    "editSentNotice",
    "notices",
    editSentSteps,
    [
      "Why can't I edit a notice I already sent?",
      "I made a typo in a sent notice",
      "There's a mistake in a notice I sent",
      "So I can't edit it at all?",
      "How do I edit a sent notice?",
      "How do I correct a sent notice?",
      "I only need instructions to correct a sent notice",
      "How do I change a notice after sending it?",
      "Can I fix a mistake in a notice I sent?",
      "I made a typo in a notice, how do I fix it?",
      "How do I correct a published notice?",
      "Can I update a notice that's already gone out?",
      "How do I edit a notice?",
      "Can I edit a notice after I send it?",
      "Can I edit a notice after I sent it?",
      "How do I resend a notice with changes?",
      "How do I copy a notice?",
      "Copy last weeks notice",
      "How do I duplicate a notice?",
      "send the same announcement again",
      "change a notice thats already out",
      "Can I copy it?",
      "can i unsend and edit",
      "wheres the duplicate button",
      "edit published notice",
    ]
  ),

  howToEntry(
    "createEvent",
    "calendar",
    "On Calendar, press ADD EVENT, or click a day. Fill in the title, date, time and Location, then press Publish.",
    [
      "How do I create an event?",
      "How do I add an event to the calendar?",
      "How do I put something on the calendar?",
      "How do I make an event?",
      "How do I add a date to the calendar?",
      "How do I add sports day to the calendar?",
      "How do events get onto the calendar?",
      "How do I add a school trip to the calendar?",
      "How do I set up a parent evening?",
      "How do I add a meeting to the calendar?",
      "OK back to the event. How do I create one again?",
      "i need a date for parents",
    ]
  ),

  howToEntry(
    "addUser",
    "users",
    "On Users, press ADD USER. Enter their email addresses or phone numbers, choose any Circles, and press Add user. Until they join, they're under Pending Invitations.",
    [
      "How do I add a user?",
      "How do I add parents?",
      "How do I invite parents?",
      "How do I invite someone to the app?",
      "How do I add a new parent?",
      "How do I get parents onto Noticeboard?",
      "How do I invite users by phone number?",
      "How do I add users?",
      "How do I invite users by email?",
      "How do I send parents an invite?",
    ]
  ),

  howToEntry(
    "createCircle",
    "circles",
    "On Circles, press CREATE CIRCLE. Give it a name, add members if you like, and press Create Sub-Circle.",
    [
      "How do I create a circle?",
      "How do I make a new circle?",
      "How do I add a circle?",
      "How do I set up a group for a class?",
      "How do I make a group for Grade 3?",
      "How do I start a new class group?",
      "How do I create a group?",
      "How do I set up a circle for the staff?",
      "How do I make a circle for a sports team?",
      "How do I create a sub-circle?",
    ]
  ),

  howToEntry(
    "addToCircle",
    "circles",
    "On Circles, open the Circle and go to ADD USERS. Press + beside the person, then Add user.",
    [
      "How do I add someone to a circle?",
      "How do I add a parent to a circle?",
      "How do I put a user in a circle?",
      "How do I add people to a class group?",
      "How do I add members to a circle?",
      "How do I add a child's parent to their class?",
      "How do I add a user to Grade 2?",
      "How do I put parents into the right group?",
      "How do I add someone to a group?",
      "How do I move a parent into a circle?",
    ]
  ),

  howToEntry(
    "addTeamMember",
    "team",
    "On Team, press ADD MEMBER. Fill in their Full name and Phone number, choose their Role and Circle access, and press Send invite.",
    [
      "How do I add a team member?",
      "How do I add a teacher?",
      "How do I add staff?",
      "How do I give a teacher access to the dashboard?",
      "How do I add a creator?",
      "How do I invite a colleague?",
      "How do I give someone access to send notices?",
      "How do I add an owner?",
      "How do I add someone to the Team?",
      "How do I let another staff member use the dashboard?",
    ]
  ),
];
